import { ParamSource, ParamType } from '@gr/core/platform/config/registry';
import type { SettingDto } from '@gr/core/platform/config/dto';

// CFG-UC-2, PREF-UC-3: «Настройки платформы» и личные настройки — логика без React: группы и
// разделы, быстрый поиск, единицы, разбор ввода.

/** CFG-UC-2.CC-6: основные параметры идут первыми и раскрыты, второстепенные — ниже и свёрнуты. */
export enum SettingsGroup {
  Primary = 'primary',
  Secondary = 'secondary',
}

export interface SettingsSection {
  /** Группа и раздел: «primary:media» — раздел может быть в обеих группах. */
  id: string;
  group: SettingsGroup;
  /** Первая часть ключа: features, server, media, ui… */
  name: string;
  settings: SettingDto[];
}

export interface SettingsGroupView {
  group: SettingsGroup;
  sections: SettingsSection[];
}

export interface SettingsView {
  groups: SettingsGroupView[];
  shown: number;
  total: number;
}

interface IndexedSetting {
  setting: SettingDto;
  group: SettingsGroup;
  section: string;
  /** Нормализованная строка поиска: ключ, слова ключа, описания, переменная. */
  haystack: string;
}

/** Предпосчитанный индекс быстрого поиска (ADR-017): строится один раз на загруженный список. */
export interface SettingsIndex {
  readonly entries: readonly IndexedSetting[];
}

export interface SettingsFilter {
  query: string;
  onlyChanged: boolean;
}

/** CFG-UC-2.CC-5: регистр и «ё»/«е» при поиске не важны. */
export function normalizeSearchText(text: string): string {
  return text.toLowerCase().replaceAll('ё', 'е');
}

/** Слова ключа: «media.maxUploadMb» → «media max upload mb» — поиск «max upload» его находит. */
export function keyWords(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[._-]+/g, ' ')
    .toLowerCase();
}

export function sectionOf(key: string): string {
  return key.split('.')[0]!;
}

export function groupOf(setting: SettingDto): SettingsGroup {
  return setting.primary ? SettingsGroup.Primary : SettingsGroup.Secondary;
}

export function sectionId(group: SettingsGroup, section: string): string {
  return `${group}:${section}`;
}

export function buildSettingsIndex(settings: readonly SettingDto[]): SettingsIndex {
  return {
    entries: settings.map((setting) => ({
      setting,
      group: groupOf(setting),
      section: sectionOf(setting.key),
      haystack: normalizeSearchText([setting.key, keyWords(setting.key), ...Object.values(setting.description), setting.env ?? ''].join('\n')),
    })),
  };
}

/** Слова запроса; параметр подходит, если в его строке поиска есть каждое. */
export function searchTerms(query: string): string[] {
  return normalizeSearchText(query).split(/\s+/).filter(Boolean);
}

/** Активен ли фильтр: тогда разделы с найденным раскрываются сами (CFG-UC-2.CC-6). */
export function isFiltering(filter: SettingsFilter): boolean {
  return filter.onlyChanged || searchTerms(filter.query).length > 0;
}

/** CFG-UC-2.CC-5, CFG-UC-2.CC-6: группы и разделы в порядке реестра, только с найденным. */
export function filterSettings(index: SettingsIndex, filter: SettingsFilter): SettingsView {
  const terms = searchTerms(filter.query);
  const groups = new Map<SettingsGroup, Map<string, SettingsSection>>([
    [SettingsGroup.Primary, new Map()],
    [SettingsGroup.Secondary, new Map()],
  ]);
  let shown = 0;
  for (const entry of index.entries) {
    if (filter.onlyChanged && !isChanged(entry.setting)) continue;
    if (!terms.every((term) => entry.haystack.includes(term))) continue;
    const sections = groups.get(entry.group)!;
    let section = sections.get(entry.section);
    if (!section) {
      section = { id: sectionId(entry.group, entry.section), group: entry.group, name: entry.section, settings: [] };
      sections.set(entry.section, section);
    }
    section.settings.push(entry.setting);
    shown += 1;
  }
  return {
    groups: [...groups.entries()].filter(([, sections]) => sections.size > 0).map(([group, sections]) => ({ group, sections: [...sections.values()] })),
    shown,
    total: index.entries.length,
  };
}

/** CFG-UC-2.CC-6: какие разделы раскрыты, пока администратор их не трогал. */
export function initiallyOpenSections(index: SettingsIndex): Set<string> {
  return new Set(index.entries.filter((entry) => entry.group === SettingsGroup.Primary).map((entry) => sectionId(entry.group, entry.section)));
}

/** CFG-UC-3: функция платформы — логический параметр, который можно переключить из системы. */
export function isToggle(setting: SettingDto): boolean {
  return setting.type === ParamType.Boolean && setting.editable && !setting.secret;
}

/** Значение не по умолчанию, ждёт перезапуска или сохранённое не применилось. */
export function isChanged(setting: SettingDto): boolean {
  return setting.source !== ParamSource.Default || setting.pendingRestart || setting.savedInvalid;
}

// Ввод значения параметра общий с личными настройками (PREF-UC-3).
export { inputText, parseSettingInput, unitOf, ValueUnit } from '../../../../shared/config/setting-input';
